import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useHasFilter } from '@/hooks/useHasFilter';

import { parseAllParameters, periodBases, TUseFiltersdReturn } from '@/ui-kit/table-filters/filters.type';

import { ISearchCard } from '@/features/cards/store/models/card.model';
import { useAllCardsTypeField, mapAllCardsTypeField } from '@/features/cards/hooks/AllCards/useAllCardsTypeField';
import { useAllCardsStatusField, mapAllCardsStatus } from '@/features/cards/hooks/AllCards/useAllCardsStatusField';
import { useAllCardsCreatedField, mapAllCardsCreated } from '@/features/cards/hooks/AllCards/useAllCardsCreatedField';
import { useAllCardsSearchField, mapAllCardsSearch } from '@/features/cards/hooks/AllCards/useAllCardsSearchField';
import { IAllCardsFiltersFormFields, TAllCardsFiltersFields } from '@/features/cards/store/types/agent-card.types';

export const parseAllCardFiltersValue = (type: keyof IAllCardsFiltersFormFields, value): ISearchCard => {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (type === 'type') {
    return mapAllCardsTypeField(value);
  } else if (type === 'status') {
    return mapAllCardsStatus(value);
  } else if (type === 'createdDate') {
    return mapAllCardsCreated(value);
  } else if (type === 'search') {
    return mapAllCardsSearch(value);
  }
};

export function useAllCardsFilters(
  onChange: (queryParameters: ISearchCard) => void,
  initialValues: Partial<IAllCardsFiltersFormFields> = {},
): TUseFiltersdReturn<TAllCardsFiltersFields> {
  const defaultValues: IAllCardsFiltersFormFields = {
    status: null,
    type: null,
    createdDate: { from: null, to: null, base: periodBases.ON },
    ...initialValues,
  };

  const methods = useForm<IAllCardsFiltersFormFields>({ defaultValues });

  const filtersDeps = JSON.stringify(initialValues);

  useEffect(() => {
    const values = JSON.parse(filtersDeps);
    Object.keys(values).map((key: keyof IAllCardsFiltersFormFields) => {
      methods.setValue(key, values[key]);
    });
  }, [filtersDeps, methods]);

  const getQueryParameters = useCallback(() => {
    const values = methods.getValues();
    return parseAllParameters<IAllCardsFiltersFormFields, ISearchCard>(values, parseAllCardFiltersValue);
  }, [methods]);

  const handleChange = useCallback(() => {
    onChange(getQueryParameters());
  }, [getQueryParameters, onChange]);

  const handleClear = useCallback(() => {
    methods.reset();
    onChange(null);
  }, [methods, onChange]);

  const status = useAllCardsStatusField(methods, handleChange);
  const type = useAllCardsTypeField(methods, handleChange);
  const createdDate = useAllCardsCreatedField(methods, handleChange);
  const search = useAllCardsSearchField(methods, handleChange);

  const hasFilters = useHasFilter({ status, type, createdDate, search });

  return { status, type, createdDate, search, clearFilters: handleClear, hasFilters };
}
